import type { components as internalComponents } from '@/generated/internal-api/openapi-types';
import type { components as messagingComponents } from '@/generated/messaging-api/openapi-types';

export type AgentType = internalComponents['schemas']['AgentType.schema'];
export type AgentDefinitionV1 = internalComponents['schemas']['AgentDefinitionV1.schema'];
export type AgentSessionSummaryV1 = internalComponents['schemas']['AgentSessionSummaryV1.schema'];
export type AgentSessionV1 = internalComponents['schemas']['AgentSessionV1.schema'];
export type AgentThreadV1 = internalComponents['schemas']['AgentThreadV1.schema'];
export type AgentEventV1 = internalComponents['schemas']['AgentEventV1.schema'];
export type AgentExecutionV1 = internalComponents['schemas']['AgentExecutionV1.schema'];
export type AgentExecutionApiId = internalComponents['schemas']['AgentExecutionApiId.schema'];

export type AgentThreadStatus = AgentThreadV1['status'];
export type AgentExecutionStatus = AgentExecutionV1['status'];
export type AgentEventType = AgentEventV1['event_type'];
export type AgentEventRole = NonNullable<AgentEventV1['role']>;

/** Payload for display_message events — the user-visible conversation content. */
export interface DisplayMessagePayload {
  content: string;
  format?: 'text' | 'markdown';
}

/** Payload for state_update events. */
export interface StateUpdatePayload {
  [key: string]: unknown;
}

/** Union of known payload shapes keyed by event_type. */
export interface AgentEventPayloadMap {
  display_message: DisplayMessagePayload;
  state_update: StateUpdatePayload;
  llm_message: Record<string, unknown>;
  request_state_update: Record<string, unknown>;
  acknowledgement: Record<string, unknown>;
  custom: Record<string, unknown>;
}

/**
 * Type guard: safely extract a typed payload from an AgentEventV1. Returns null
 * when the event_type does not match or the payload lacks the expected shape.
 */
export const parseEventPayload = <T extends AgentEventType>(
  event: AgentEventV1,
  expectedType: T
): AgentEventPayloadMap[T] | null => {
  if (event.event_type !== expectedType) {
    return null;
  }
  const payload = event.payload as unknown as Record<string, unknown>;
  if (expectedType === 'display_message' && typeof payload?.content !== 'string') {
    return null;
  }
  return payload as AgentEventPayloadMap[T];
};

/**
 * Extract display text from an AgentEventV1, with fallback for unknown shapes.
 * Returns null if the event has no displayable content.
 */
export const getDisplayContent = (event: AgentEventV1): string | null => {
  if (event.event_type === 'display_message') {
    const payload = event.payload as unknown as Record<string, unknown>;
    if (typeof payload?.content === 'string') {
      return payload.content;
    }
    return JSON.stringify(payload);
  }
  return null;
};

type GeneratedAgentStreamEventV1 = messagingComponents['schemas']['AgentStreamEventV1.schema'];

export type AgentStreamEventType = GeneratedAgentStreamEventV1['type'];

export const TERMINAL_STREAM_EVENTS: ReadonlySet<AgentStreamEventType> = new Set(['completed', 'failed', 'cancelled']);

type WithId<T> = T extends unknown ? T & { id?: string } : never;

/**
 * Stream event delivered over SSE. The optional `id` is injected by useAgentStream
 * from the SSE lastEventId so the reducer can deduplicate replays on reconnect.
 */
export type AgentStreamEventV1 = WithId<GeneratedAgentStreamEventV1>;

export type AgentSessionPhase = 'idle' | 'creating' | 'streaming' | 'completed' | 'failed' | 'cancelled';

export interface StreamingText {
  executionApiId: AgentExecutionApiId;
  accumulatedText: string;
  isStreaming: boolean;
}
